/**
 * Czech Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const cs: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Přehled',
  'nav.funds': 'Fondy',
  'nav.investments': 'Investice',
  'nav.investees': 'Příjemci investic',
  'nav.esg': 'ESG Přehled',
  'nav.compliance': 'Soulad',
  'nav.reports': 'Zprávy',
  'nav.settings': 'Nastavení',
  'nav.users': 'Uživatelé',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Ukazatele',
  'nav.taxonomy': 'Taxonomie EU',
  'nav.filings': 'Podání',
  'nav.calendar': 'Kalendář',
  'nav.submit': 'Odeslat data',
  'nav.submissions': 'Moje podání',
  'nav.benchmarks': 'Referenční hodnoty',
  'nav.messages': 'Zprávy',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Výkonnost fondů',
  'nav.esgSummary': 'ESG Souhrn',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Hloubková kontrola',
  'nav.impact': 'Ukazatele dopadu',
  'nav.sdg': 'Mapování SDGs',
  'nav.audit': 'Auditní stopa',
  'nav.snapshots': 'Datové snímky',
  'nav.knowledgeGraph': 'Graf znalostí',
  'nav.geographic': 'Geografický',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Asistent',
  'nav.tasks': 'Úkoly',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorní monitor',

  // Common Actions
  'action.create': 'Vytvořit',
  'action.edit': 'Upravit',
  'action.delete': 'Smazat',
  'action.save': 'Uložit',
  'action.cancel': 'Zrušit',
  'action.submit': 'Odeslat',
  'action.approve': 'Schválit',
  'action.reject': 'Zamítnout',
  'action.download': 'Stáhnout',
  'action.export': 'Exportovat',
  'action.import': 'Importovat',
  'action.filter': 'Filtrovat',
  'action.search': 'Hledat',
  'action.refresh': 'Obnovit',
  'action.close': 'Zavřít',
  'action.back': 'Zpět',
  'action.next': 'Další',
  'action.previous': 'Předchozí',
  'action.viewAll': 'Zobrazit vše',
  'action.viewDetails': 'Zobrazit detaily',
  'action.generate': 'Generovat',
  'action.signOut': 'Odhlásit se',
  'action.addNew': 'Přidat nové',
  'action.invite': 'Pozvat',

  // Statuses
  'status.draft': 'Koncept',
  'status.submitted': 'Odesláno',
  'status.validated': 'Validováno',
  'status.approved': 'Schváleno',
  'status.rejected': 'Zamítnuto',
  'status.pending': 'Čekající',
  'status.inProgress': 'Probíhá',
  'status.overdue': 'Po termínu',
  'status.compliant': 'V souladu',
  'status.filed': 'Podáno',
  'status.active': 'Aktivní',
  'status.inactive': 'Neaktivní',
  'status.closed': 'Uzavřeno',
  'status.fundraising': 'Získávání prostředků',
  'status.investing': 'Investování',
  'status.harvesting': 'Sklizeň',
  'status.validating': 'Validace',
  'status.review': 'V přezkumu',
  'status.monitoring': 'Monitorování',
  'status.exiting': 'Výstup',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Hloubková kontrola',
  'status.disbursed': 'Vyplaceno',
  'status.restructured': 'Restrukturalizováno',
  'status.matured': 'Splatné',
  'status.written_off': 'Odepsáno',
  'status.exited': 'Vystoupil',
  'status.incomplete': 'Neúplné',
  'status.open': 'Otevřené',
  'status.in_progress': 'Probíhá',
  'status.completed': 'Dokončeno',
  'status.cancelled': 'Zrušeno',
  'status.accepted': 'Přijato',
  'status.failed': 'Selhalo',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Přehled portfolia',
  'dashboard.complianceStatus': 'Stav souladu',
  'dashboard.collectionProgress': 'Průběh sběru',
  'dashboard.activityFeed': 'Aktivita',
  'dashboard.quickActions': 'Rychlé akce',
  'dashboard.investmentPipeline': 'Investiční pipeline',
  'dashboard.totalFunds': 'Celkem fondů',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktivní investice',
  'dashboard.totalInvestees': 'Příjemci investic',
  'dashboard.recentActivity': 'Nedávná aktivita',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Celkem závazků',
  'lp.capitalCalled': 'Vyžádaný kapitál',
  'lp.distributed': 'Distribuováno',
  'lp.netPosition': 'Čistá pozice',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Výkonnost fondů',
  'lp.esgSummary': 'ESG Souhrn',
  'lp.reportLibrary': 'Knihovna zpráv',
  'lp.taxonomyAligned': 'Soulad s taxonomií',
  'lp.paiCoverage': 'PAI Pokrytí',
  'lp.portfolioPerformance': 'Výkonnost LP portfolia',

  // Fund Labels
  'fund.name': 'Název fondu',
  'fund.code': 'Kód fondu',
  'fund.vintage': 'Rok vzniku',
  'fund.size': 'Velikost fondu',
  'fund.targetSize': 'Cílová velikost',
  'fund.currency': 'Měna',
  'fund.strategy': 'Strategie',
  'fund.status': 'Stav',
  'fund.sfdrCategory': 'SFDR Kategorie',
  'fund.geographyFocus': 'Geografické zaměření',
  'fund.sectorFocus': 'Sektorové zaměření',

  // Form Labels
  'form.name': 'Název',
  'form.email': 'E-mail',
  'form.country': 'Země',
  'form.amount': 'Částka',
  'form.currency': 'Měna',
  'form.date': 'Datum',
  'form.description': 'Popis',
  'form.notes': 'Poznámky',
  'form.type': 'Typ',
  'form.period': 'Období',
  'form.year': 'Rok',
  'form.required': 'Povinné',
  'form.optional': 'Nepovinné',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Závazek',
  'table.called': 'Vyžádáno',
  'table.distributed': 'Distribuováno',
  'table.tvpi': 'TVPI',
  'table.status': 'Stav',
  'table.regulation': 'Regulace',
  'table.filingType': 'Typ podání',
  'table.reportingPeriod': 'Období',
  'table.createdAt': 'Vytvořeno',
  'table.actions': 'Akce',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomie EU',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Úplnost dat',
  'compliance.nextDeadline': 'Příští termín',

  // Reports
  'report.quarterly': 'Čtvrtletní zpráva',
  'report.annual': 'Výroční zpráva',
  'report.paiStatement': 'PAI Prohlášení',
  'report.taxonomyReport': 'Zpráva o taxonomii',
  'report.generatedAt': 'Vygenerováno',
  'report.version': 'Verze',

  // General
  'general.loading': 'Načítání...',
  'general.noData': 'Žádná dostupná data',
  'general.error': 'Došlo k chybě',
  'general.retry': 'Zkusit znovu',
  'general.total': 'Celkem',
  'general.page': 'Stránka',
  'general.of': 'z',
  'general.showing': 'Zobrazeno',
  'general.results': 'výsledků',
  'general.language': 'Jazyk',
  'general.english': 'Angličtina',
  'general.french': 'Francouzština',

  // Tour
  'tour.takeTour': 'Spustit prohlídku',
  'tour.skip': 'Přeskočit',
  'tour.next': 'Další',
  'tour.prev': 'Předchozí',
  'tour.finish': 'Dokončit',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigace',
  'tour.sidebarContent': 'Použijte postranní panel k navigaci mezi správou portfolia, sběrem dat ESG, dodržováním předpisů, reporty a analytickými nástroji.',
  'tour.kpisTitle': 'Přehled portfolia',
  'tour.kpisContent': 'Tyto karty ukazují vaše portfolio na první pohled — celkové fondy, AUM, aktivní investice a počet subjektů.',
  'tour.actionsTitle': 'Rychlé akce',
  'tour.actionsContent': 'Vytvářejte fondy, přidávejte investice, zvěte subjekty a generujte reporty jedním kliknutím.',
  'tour.languageTitle': 'Jazyk',
  'tour.languageContent': 'Přepínejte mezi 24 oficiálními jazyky EU. Celé rozhraní se přeloží okamžitě.',
  'tour.aiTitle': 'AI Asistent',
  'tour.aiContent': 'Ptejte se na své portfolio kdykoli. AI asistent má plný kontext o vašich fondech a investicích.',
  'action.createFund': 'Vytvořit fond',
  'action.editFund': 'Upravit fond',
  'action.addInvestment': 'Přidat investici',
  'action.addInvestee': 'Přidat subjekt',
  'action.editInvestee': 'Upravit subjekt',
  'action.createTemplate': 'Vytvořit šablonu',
  'action.createSubmission': 'Vytvořit podání',
  'action.createFiling': 'Vytvořit podání',
  'action.generateReport': 'Generovat report',
  'action.inviteUser': 'Pozvat uživatele',
  'action.sendInvitation': 'Odeslat pozvánku',
  'action.saveChanges': 'Uložit změny',
  'form.fundName': 'Název fondu',
  'form.fundCode': 'Kód fondu',
  'form.sfdrCategory': 'SFDR Kategorie',
  'form.baseCurrency': 'Základní měna',
  'form.targetSize': 'Cílová velikost',
  'form.vintageYear': 'Rok založení',
  'form.geographyFocus': 'Geografické zaměření',
  'form.sectorFocus': 'Sektorové zaměření',
  'form.investmentCode': 'Kód investice',
  'form.instrumentType': 'Typ nástroje',
  'form.maturityDate': 'Datum splatnosti',
  'form.sector': 'Sektor',
  'form.countryCode': 'Kód země',
  'form.entityType': 'Typ subjektu',
  'form.region': 'Region',
  'form.staffCount': 'Počet zaměstnanců',
  'form.borrowers': 'Dlužníci',
  'form.contactName': 'Jméno kontaktu',
  'form.contactEmail': 'Kontaktní e-mail',
  'form.templateName': 'Název šablony',
  'form.templateType': 'Typ šablony',
  'form.indicators': 'Ukazatele',
  'form.reportingPeriod': 'Reportovací období',
  'form.reportingYear': 'Rok reportu',
  'form.regulation': 'Regulace',
  'form.filingType': 'Typ podání',
  'form.reportType': 'Typ reportu',
  'form.emailAddress': 'E-mailová adresa',
  'form.role': 'Role',
  'form.selectFund': 'Vyberte fond...',
  'form.selectInvestee': 'Vyberte subjekt...',
  'table.code': 'Kód',
  'table.investee': 'Subjekt',
  'table.instrument': 'Nástroj',
  'table.amount': 'Částka',
  'table.currency': 'Měna',
  'table.maturity': 'Splatnost',
  'table.name': 'Název',
  'table.country': 'Země',
  'table.type': 'Typ',
  'table.period': 'Období',
  'table.completion': 'Dokončení',
  'table.submitted': 'Odesláno',
  'table.version': 'Verze',
  'table.indicators': 'Ukazatele',
  'table.dueDate': 'Termín',
  'table.generated': 'Vygenerováno',
  'table.email': 'E-mail',
  'table.role': 'Role',
  'error.createFailed': 'Vytvoření se nezdařilo. Zkuste to znovu.',
  'error.updateFailed': 'Aktualizace se nezdařila. Zkuste to znovu.',
  'error.loadFailed': 'Načtení dat se nezdařilo.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFOLIO',
  'sections.dataCollection': 'SBĚR DAT',
  'sections.intelligence': 'ZPRAVODAJSTVÍ',
  'sections.ecosystem': 'EKOSYSTÉM',
  'sections.admin': 'SPRÁVA',
  'sections.other': 'OSTATNÍ',

  // Page Titles
  'pages.messagesTitle': 'Zprávy',
  'pages.taxonomyTitle': 'Taxonomie EU',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Graf znalostí',
  'pages.benchmarksTitle': 'Benchmarky',
  'pages.mcpTitle': 'Integrace MCP',
  'pages.tasksTitle': 'Úkoly',
  'pages.submitDataTitle': 'Odeslat ESG data',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarky již brzy',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Taxonomie EU',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Ostatní',
  'labels.critical': 'Kritický',
  'labels.high': 'Vysoký',
  'labels.medium': 'Střední',
  'labels.low': 'Nízký',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
